import { useEffect, type RefObject } from 'react'
import type { RippleMarkHandle } from './ripple-mark'

/**
 * «Av og til» for merket i appskallet.
 *
 * Gimmicken skal være et lite gjenkjennelsestegn, ikke en tikkende animasjon i
 * øyekroken. Den spilles derfor bare tre steder, og aldri på timer:
 *
 *  - når skallet monteres, altså én gang per lasting,
 *  - når fanen kommer tilbake etter å ha vært borte lenge nok til at man har
 *    vært et annet sted, ikke bare byttet vindu et øyeblikk,
 *  - når man trykker på merket, som er den eneste gangen brukeren ber om den.
 *
 * Og aldri mens noen skriver. En animasjon som starter midt i et skjemafelt
 * stjeler oppmerksomheten på det verst tenkelige tidspunktet.
 *
 * Redusert bevegelse trenger ikke sjekkes her: `RippleMark.replay` er allerede
 * en nullhandling da, så regelen står ett sted i stedet for to.
 */

/** Hvor lenge fanen må ha vært borte før retur teller som «tilbake igjen». */
const AWAY_MS = 5 * 60 * 1000

/** Pusterom etter montering, så merket ikke konkurrerer med at siden tegnes. */
const MOUNT_DELAY_MS = 320

function isTyping(): boolean {
  const el = document.activeElement
  if (!el) return false
  const tag = el.tagName
  return (
    tag === 'INPUT' ||
    tag === 'TEXTAREA' ||
    tag === 'SELECT' ||
    (el as HTMLElement).isContentEditable
  )
}

export function useOccasionalRipple(
  ref: RefObject<RippleMarkHandle | null>,
  enabled = true
) {
  useEffect(() => {
    if (!enabled) return

    const play = () => {
      if (isTyping()) return
      ref.current?.replay()
    }

    const mountTimer = window.setTimeout(play, MOUNT_DELAY_MS)

    let hiddenAt: number | null = null
    const onVisibility = () => {
      if (document.hidden) {
        hiddenAt = Date.now()
        return
      }
      if (hiddenAt !== null && Date.now() - hiddenAt >= AWAY_MS) play()
      hiddenAt = null
    }

    document.addEventListener('visibilitychange', onVisibility)
    return () => {
      window.clearTimeout(mountTimer)
      document.removeEventListener('visibilitychange', onVisibility)
    }
  }, [ref, enabled])
}
